import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TabBar from './TabBar';

// 假設相簿圖片從 01.webp 到 12.webp (皆為 3:2)
const albumImages = Array.from({ length: 12 }, (_, i) =>
  `/images/album/${(i + 1).toString().padStart(2, '0')}.webp`
);

const PLAY_DURATION = 3000; // 每張播放 3000ms
const BOUNCE_DURATION = 500; // 回彈動畫 500ms

// 輔助函式：大聲公告限字數固定為 30，不分語言
function getMaxShoutCount(value) {
  return 30;
}

function Preloader() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-[#fd574b] z-50">
      <img
        src="/images/page0.webp"
        alt="Landing"
        className="object-contain mx-auto"
        style={{ maxWidth: '450px', maxHeight: '300px' }}
        loading="lazy"
      />
      <div className="text-white text-2xl mt-4">載入中，請稍候…</div>
    </div>
  );
}

/* ----- 輔助函式：humpOpacity ----- */
function humpOpacity(scroll, start, end) {
  const range = end - start;
  if (range === 0) return 0.5;
  const mid = start + range / 2;
  if (scroll <= start) return 0.5;
  if (scroll >= end) return 0.5;
  if (scroll <= mid) {
    return 0.5 + 0.5 * ((scroll - start) / (mid - start));
  } else {
    return 1 - 0.5 * ((scroll - mid) / (end - mid));
  }
}

/* ----- Modal 組件 ----- */
function CustomModal({ open, mode, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white p-6 rounded text-gray-900 w-80"
        style={{ animation: 'shake 0.5s', animationIterationCount: 1 }}
      >
        <p className="whitespace-pre-line text-lg text-center">{message}</p>
        <div className="mt-4 flex justify-center space-x-4">
          {mode === "general" ? (
            <button onClick={onConfirm} className="px-4 py-2 bg-blue-600 text-white rounded">
              好
            </button>
          ) : (
            <>
              <button onClick={onConfirm} className="px-4 py-2 bg-red-500 text-white rounded">
                殘忍拒絕
              </button>
              <button onClick={onCancel} className="px-4 py-2 bg-green-500 text-white rounded">
                好啦！填一下！
              </button>
            </>
          )}
        </div>
      </div>
      <style>{`
        @keyframes shake {
          0% { transform: translate(0px, 0px); }
          20% { transform: translate(-10px, 0px); }
          40% { transform: translate(10px, 0px); }
          60% { transform: translate(-10px, 0px); }
          80% { transform: translate(10px, 0px); }
          100% { transform: translate(0px, 0px); }
        }
      `}</style>
    </div>
  );
}

function ConfirmModal({ open, summary, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded text-gray-900 w-11/12 max-w-2xl">
        <h2 className="text-xl font-bold mb-4 text-center">請確認您的資料</h2>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '80vh', overflowY: 'auto' }} className="text-lg bg-gray-100 p-4 rounded">
          {summary.trim()}
        </pre>
        <div className="mt-4 flex justify-center space-x-4">
          <button onClick={onConfirm} className="px-4 py-2 bg-blue-600 text-white rounded">
            確認送出
          </button>
          <button onClick={onCancel} className="px-4 py-2 bg-gray-600 text-white rounded">
            返回修改
          </button>
        </div>
      </div>
    </div>
  );
}

function ElectronicModal({ open, onSubmit, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded text-gray-900 w-72" style={{ maxWidth: '300px' }}>
        <h2 className="text-xl text-center mb-4">電子喜帖預覽</h2>
        <div className="flex justify-center">
          <img src="/images/Electronic_Wedding_Invitations.jpg" alt="電子喜帖" className="max-w-full h-auto" />
        </div>
        <div className="mt-4 flex justify-center space-x-4">
          <a
            href="/images/Electronic_Wedding_Invitations.jpg"
            download="Electronic_Wedding_Invitations.jpg"
            className="px-4 py-2 bg-green-600 text-white rounded"
          >
            下載喜帖
          </a>
          <button onClick={onClose} className="px-4 py-2 bg-red-500 text-white rounded">
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----- 大聲公留言輪播元件 ----- */
function ShoutBox({ shoutMessages, isLandscape, currentIndexRef}) {
  // 過濾掉空白的訊息
  const validMessages = useMemo(
    () => shoutMessages.filter(msg => msg.message.trim() !== ""),
    [shoutMessages]
  );
  const [displayedMessages, setDisplayedMessages] = useState([]);

  if (isLandscape) {
    // 橫屏模式：以堆疊方式顯示訊息（每三秒新增一則）
    const [displayedMessages, setDisplayedMessages] = useState([]);

    useEffect(() => {
      if (validMessages.length > 0) {
        let index = currentIndexRef.current;
        const interval = setInterval(() => {
          // 每三秒新增一則訊息
          currentIndexRef.current = index;
          setDisplayedMessages(prev => [...prev, validMessages[index]]);
          index++;
          // 如果有效訊息少於9則只播放一次，播放完後停止計時器
          if (validMessages.length < 9 && index >= validMessages.length) {
            clearInterval(interval);
          } else if (validMessages.length >= 9) {
            // 如果有效訊息數大於或等於9，則循環播放訊息
            index = index % validMessages.length;
          }
        }, 3000);
        return () => clearInterval(interval);
      }
    }, [validMessages]);

    const containerStyle = {
      position: 'fixed',
      bottom: '1rem',
      left: '1rem',
      width: '300px',
      height: '300px',
      background: 'rgba(128,128,128,0.25)',
      padding: '16px',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '1rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      overflowY: 'auto',
      zIndex: 1000
    };

    return (
      <div style={containerStyle}>
        {displayedMessages.length > 0 ? (
          displayedMessages.map((msg, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              {msg.name.trim() !== "" ? (
                <>
                  <span style={{ color: '#5500FF' }}>{msg.name}：</span>
                  {msg.message}
                </>
              ) : (
                msg.message
              )}
            </div>
          ))
        ) : (
          ""
        )}
      </div>
    );
  } else {
    // 直屏模式：恢復原本跑馬燈的效果
    const marqueeText = validMessages.length > 0
      ? validMessages
          .map(msg => (msg.name.trim() !== "" ? `${msg.name}：${msg.message}` : msg.message))
          .join("　　　　　　　　　　　　　　　")
      : "";
    const containerStyle = {
      position: 'fixed',
      bottom: '5rem', // 底部留5rem
      left: '1rem',
      background: 'rgba(128,128,128,0.25)',
      padding: '4px 12px', // 垂直 padding 調整以符合單行高度
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '1rem',
      height: '2em',  // 只有一行文字的高度
      overflow: 'hidden', // 隱藏超出容器的部分
      whiteSpace: 'nowrap',
      zIndex: 1000,
      width: 'calc(100% - 2rem)'
    };

    const duration = Math.max(10, marqueeText.length * 0.5);
    const marqueeTextStyle = {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      animation: `marquee ${duration}s linear infinite`,
      animationDelay: `-${duration / 2}s`
    };

    return (
      <>
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
        `}</style>
        <div style={containerStyle}>
          {marqueeText && <div style={marqueeTextStyle}>{marqueeText}</div>}
        </div>
      </>
    );
  }
}

/* ----- 管理員登入 Modal ----- */
function AdminLoginModal({ open, onLogin, onCancel, adminUsername, setAdminUsername, adminPassword, setAdminPassword }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded text-gray-900 w-80">
        <h2 className="text-xl font-bold mb-4 text-center">管理員登入</h2>
        <div className="mb-2">
          <label className="block mb-1">帳號:</label>
          <input
            type="text"
            value={adminUsername}
            onChange={(e) => setAdminUsername(e.target.value)}
            className="w-full border p-1"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1">密碼:</label>
          <input
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            className="w-full border p-1"
          />
        </div>
        <div className="flex justify-center space-x-4">
          <button onClick={onLogin} className="px-4 py-2 bg-blue-600 text-white rounded">登入</button>
          <button onClick={onCancel} className="px-4 py-2 bg-gray-600 text-white rounded">取消</button>
        </div>
      </div>
    </div>
  );
}

function downloadCSV(data) {
  if (!data || data.length === 0) return;

  // 定義 CSV 標題
  const headers = [
    "編號",
    "姓名",
    "出席",
    "關係",
    "抽捧花",
    "葷",
    "素",
    "兒童",
    "兒童座椅",
    "喜帖形式",
    "喜帖細項",
    "地址",
    "悄悄話",
    "大聲公",
    "加入Line",
    "建立時間"
  ];

  // 轉換每一筆資料
  const rows = data.map(item => [
    item.id,
    item.name,
    item.attendance,
    item.relationship || '-',
    item.wantBouquet ? '是' : '否',
    item.meatCount,
    item.vegCount,
    item.childCount,
    item.childSeatCount,
    item.invitationType,
    item.invitationDetail || '-',
    item.address || '-',
    item.privateMessage || '-',
    item.shout || '-',
    item.joinLine ? '是' : '否',
    item.created_at ? new Date(item.created_at).toLocaleString() : '-'
  ]);

  // 組合 CSV 內容
  const csvRows = [];
  csvRows.push(headers.join(",")); // 標題
  rows.forEach(row => {
    // 每個欄位用雙引號包住
    csvRows.push(row.map(value => `"${value}"`).join(","));
  });
  // 在字串最前面加入 BOM (UTF-8) 以避免中文亂碼
  const csvString = "\uFEFF" + csvRows.join("\n");

  // 建立 blob 並觸發下載
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "rsvp_data.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ModifyWantBouquetModal({ open, currentValue, onConfirm, onCancel }) {
  // 如果 currentValue 為 1，轉換為 "yes"；如果為 0，轉換為 "no"
  const convertValue = (val) => {
    if (val === 1) return "yes";
    if (val === 0) return "no";
  };
  const [selectedValue, setSelectedValue] = useState(currentValue);

  // 當 currentValue 改變時，同步更新
  useEffect(() => {
    setSelectedValue(convertValue(currentValue));
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded text-gray-900 w-80">
        <p className="text-center mb-4">
          由於新郎的技術不到位<br/>
          沒錯，就是那位笨笨的哲儀！<br/>
          還請各位新娘閨蜜們，<br/>
          幫忙確認是否抽捧花，<br/>
          非常感謝您的諒解與支持！<br/>
          請選擇：
        </p>
        <div className="flex justify-around mb-4">
          <label>
            <input
              type="radio"
              name="modifyWantBouquet"
              value="yes"
              checked={selectedValue === "yes"}
              onChange={() => setSelectedValue("yes")}
            />
            <span className="ml-1">是</span>
          </label>
          <label>
            <input
              type="radio"
              name="modifyWantBouquet"
              value="no"
              checked={selectedValue === "no"}
              onChange={() => setSelectedValue("no")}
            />
            <span className="ml-1">否</span>
          </label>
        </div>
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => onConfirm(selectedValue)}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            確認
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-600 text-white rounded"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};


/* ----- 管理員面板 Modal ----- */
function AdminPanelModal({ open, responses, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] overflow-auto">
      <div className="bg-white p-4 rounded text-gray-900 max-w-6xl w-full max-h-screen overflow-auto">
        <h2 className="text-xl font-bold mb-4 text-center">所有回覆訊息</h2>
        <div className="flex justify-end mb-4">
          <button
            onClick={() => downloadCSV(responses)}
            className="px-4 py-2 bg-green-600 text-white rounded"
          >
            下載 CSV
          </button>
        </div>
        <table className="min-w-full border">
          <thead>
            <tr>
              <th className="border p-2">編號</th>
              <th className="border p-2">姓名</th>
              <th className="border p-2">出席</th>
              <th className="border p-2">關係</th>
              <th className="border p-2">抽捧花</th>
              <th className="border p-2">葷</th>
              <th className="border p-2">素</th>
              <th className="border p-2">兒童</th>
              <th className="border p-2">兒童座椅</th>
              <th className="border p-2">喜帖形式</th>
              <th className="border p-2">喜帖細項</th>
              <th className="border p-2">地址</th>
              <th className="border p-2">悄悄話</th>
              <th className="border p-2">大聲公</th>
              <th className="border p-2">加入Line</th>
              <th className="border p-2">建立時間</th>
            </tr>
          </thead>
          <tbody>
            {responses.map((item, idx) => (
              <tr key={idx}>
                <td className="border p-2">{item.id}</td>
                <td className="border p-2">{item.name}</td>
                <td className="border p-2">{item.attendance}</td>
                <td className="border p-2">{item.relationship || '-'}</td>
                <td className="border p-2">{item.wantBouquet ? '是' : '否'}</td>
                <td className="border p-2">{item.meatCount}</td>
                <td className="border p-2">{item.vegCount}</td>
                <td className="border p-2">{item.childCount}</td>
                <td className="border p-2">{item.childSeatCount}</td>
                <td className="border p-2">{item.invitationType}</td>
                <td className="border p-2">{item.invitationDetail || '-'}</td>
                <td className="border p-2">{item.address || '-'}</td>
                <td className="border p-2">{item.privateMessage || '-'}</td>
                <td className="border p-2">{item.shout || '-'}</td>
                <td className="border p-2">{item.joinLine ? '是' : '否'}</td>
                <td className="border p-2">
                  {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-center mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-red-500 text-white rounded">
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
/* ----- Home 組件 ----- */
export default function Home() {
  // 為全站設定以隸書體為優先的字型
  const globalFont = { fontFamily: '"Apple LiSu", "Pixel LiSu", "Android LiSu", "LiSu", "隸書", serif' };

  const modalRef = useRef(null);
  const currentIndexRef = useRef(0); // 儲存目前輪播的 index

  // 在組件內部宣告 state
  const [isLoaded, setIsLoaded] = useState(false);

  // 新增管理員相關狀態
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [allResponses, setAllResponses] = useState([]);

  // 將 viewport 高度轉換為 state
  const [vh, setVh] = useState(window.innerHeight);
  useEffect(() => {
    const handleResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 當視窗高度小於此閥值時，不使用頁面漸淡效果
  const FADE_THRESHOLD = 500;
  const shouldFade = vh > FADE_THRESHOLD;

  // 滾動監控與透明度設定
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    setScrollY(window.scrollY);
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 根據 shouldFade 決定是否使用漸淡效果
  const section1Opacity = shouldFade ? (scrollY < vh ? 1 - scrollY / vh : 0) : 1;
  const section2Opacity = shouldFade ? (
    scrollY <= vh ? scrollY / vh : scrollY <= 2 * vh ? 1 - (scrollY - vh) / vh : 0
  ) : 1;
  const section3Opacity = shouldFade ? (
    scrollY < vh ? 0 : scrollY <= 2 * vh ? (scrollY - vh) / vh : scrollY <= 3 * vh ? 1 - (scrollY - 2 * vh) / vh : 0
  ) : 1;
  const section4Opacity = shouldFade ? (
    scrollY < 2 * vh ? 0 : scrollY <= 3 * vh ? (scrollY - 2 * vh) / vh : scrollY <= 4 * vh ? 1 - (scrollY - 3 * vh) / vh : 0
  ) : 1;

  const storyText = `　　我們自認平凡，沒有華麗的外表與虛浮的夢想，卻因命運的安排得以相遇相知。除了有幸遇見彼此，我們更覺得能夠認識在座每一位朋友都是我們三生有幸。
　　今天，我們衷心邀請在我們生命中重要的你們，共同見證這段樸實而真摯的情誼，與我們一同分享這份來自平凡中的不凡喜悅。期待與你們一同走過未來更多的春夏秋冬，成為我們未來日子裡最美好的記憶。`;
  const weddingInfoText = `日期：2025 / 06 / 07（週六）
時間：中午 12 : 00 準時入場
地點：臺中全國大飯店草悟廳
地址：臺中市西區館前路57號`;

  // Album 相關
  const [albumIndex, setAlbumIndex] = useState(0);
  const autoTimer = useRef(null);
  const autoStartTimeRef = useRef(performance.now());
  const [transitionDuration, setTransitionDuration] = useState(0.5);
  useLayoutEffect(() => { autoStartTimeRef.current = performance.now(); }, [albumIndex]);
  useEffect(() => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    if (albumIndex === albumImages.length - 1) {
      autoTimer.current = setTimeout(() => {
        setTimeout(() => {
          setAlbumIndex(0);
          autoStartTimeRef.current = performance.now();
        }, BOUNCE_DURATION);
      }, PLAY_DURATION);
    } else {
      autoTimer.current = setTimeout(() => {
        setAlbumIndex(albumIndex + 1);
        autoStartTimeRef.current = performance.now();
      }, PLAY_DURATION);
    }
    return () => clearTimeout(autoTimer.current);
  }, [albumIndex]);
  const thumbContainerRef = useRef(null);
  const thumbRefs = useRef([]);
  const scrollAnimationFrameRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isManualRebound, setIsManualRebound] = useState(false);
  const runAutoAlignment = () => {
    const container = thumbContainerRef.current;
    const activeThumb = thumbRefs.current[albumIndex];
    const nextThumb = thumbRefs.current[(albumIndex + 1) % albumImages.length];
    if (!container || !activeThumb || !nextThumb) return;
    const now = performance.now();
    let progress = (now - autoStartTimeRef.current) / PLAY_DURATION;
    if (progress > 1) progress = 1;
    const offsetAdjustment = activeThumb.offsetWidth * 0.2;
    const expected =
      activeThumb.offsetLeft + progress * (nextThumb.offsetLeft - activeThumb.offsetLeft) - offsetAdjustment;
    container.scrollLeft = Math.round(expected);
    if (!isHovering && !isManualRebound && progress < 1) {
      scrollAnimationFrameRef.current = requestAnimationFrame(runAutoAlignment);
    }
  };
  useEffect(() => {
    let timer;
    if (!isHovering && !isManualRebound) {
      if (albumIndex === albumImages.length - 1) {
        timer = setTimeout(() => {
          const container = thumbContainerRef.current;
          const firstThumb = thumbRefs.current[0];
          if (container && firstThumb) {
            animateToTarget(firstThumb.offsetLeft, BOUNCE_DURATION);
          }
        }, PLAY_DURATION);
      } else {
        runAutoAlignment();
      }
    }
    return () => {
      if (timer) clearTimeout(timer);
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
        scrollAnimationFrameRef.current = null;
      }
    };
  }, [albumIndex, isHovering, isManualRebound]);
  const animateToTarget = (target, duration) => {
    const container = thumbContainerRef.current;
    if (!container) return;
    const start = container.scrollLeft;
    const change = target - start;
    const startTime = performance.now();
    const animate = () => {
      const now = performance.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollLeft = start + change * progress;
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    animate();
  };
  const handleThumbMouseEnter = () => {
    setIsHovering(true);
    if (scrollAnimationFrameRef.current) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
  };
  const edgeScrollRequestId = useRef(null);
  const handleThumbMouseMove = (e) => {
    handleThumbMouseEnter();
    const container = thumbContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const threshold = 80;
    if (x < threshold) {
      if (!edgeScrollRequestId.current) {
        const scrollLeft = () => {
          container.scrollLeft -= 5;
          edgeScrollRequestId.current = requestAnimationFrame(scrollLeft);
        };
        scrollLeft();
      }
    } else if (x > rect.width - threshold) {
      if (!edgeScrollRequestId.current) {
        const scrollRight = () => {
          container.scrollLeft += 5;
          edgeScrollRequestId.current = requestAnimationFrame(scrollRight);
        };
        scrollRight();
      }
    } else {
      if (edgeScrollRequestId.current) {
        cancelAnimationFrame(edgeScrollRequestId.current);
        edgeScrollRequestId.current = null;
      }
    }
  };
  const handleThumbMouseLeave = () => {
    setIsHovering(false);
    setIsManualRebound(true);
    if (edgeScrollRequestId.current) {
      cancelAnimationFrame(edgeScrollRequestId.current);
      edgeScrollRequestId.current = null;
    }
    const container = thumbContainerRef.current;
    const activeThumb = thumbRefs.current[albumIndex];
    const nextThumb = thumbRefs.current[(albumIndex + 1) % albumImages.length];
    if (!container || !activeThumb || !nextThumb) return;
    const now = performance.now();
    let progress = (now + BOUNCE_DURATION - autoStartTimeRef.current) / PLAY_DURATION;
    if (progress > 1) progress = 1;
    const expected = activeThumb.offsetLeft + progress * (nextThumb.offsetLeft - activeThumb.offsetLeft);
    animateToTarget(expected, BOUNCE_DURATION);
    setTimeout(() => {
      setIsManualRebound(false);
      if (!isHovering) {
        runAutoAlignment();
      }
    }, BOUNCE_DURATION);
  };
  const renderMainImage = () => (
    <AnimatePresence exitBeforeEnter>
      <motion.img
        key={albumIndex}
        src={albumImages[albumIndex]}
        alt={`Album ${albumIndex + 1}`}
        className="w-full h-full object-contain"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0 } }}
        transition={{ duration: transitionDuration, ease: [0.42, 0, 0.58, 1] }}
      />
    </AnimatePresence>
  );

  /* ---------------------- 表單狀態 ---------------------- */
  const [nameField, setNameField] = useState('');
  const [attend, setAttend] = useState('');
  const [meatCount, setMeatCount] = useState(0);
  const [vegCount, setVegCount] = useState(0);
  const [childCount, setChildCount] = useState(0);
  const [childSeatCount, setChildSeatCount] = useState(0);
  useEffect(() => {
    if (childCount === 0) {
      setChildSeatCount(0);
    } else if (childSeatCount > childCount) {
      setChildSeatCount(childCount);
    }
  }, [childCount, childSeatCount]);
  const [invitationType, setInvitationType] = useState('');
  const [invitationDetail, setInvitationDetail] = useState('');
  const [address, setAddress] = useState('');
  const [privateMessage, setPrivateMessage] = useState('');
  const [shout, setShout] = useState('');
  const [joinLine, setJoinLine] = useState(true);
  const mealGridClass = childCount > 0 ? "grid grid-cols-4 gap-4" : "grid grid-cols-3 gap-4";
  const [relationship, setRelationship] = useState('');
  const [wantBouquet, setWantBouquet] = useState('');
  const [allowEmptyPrivate, setAllowEmptyPrivate] = useState(false);
  const [allowEmptyShout, setAllowEmptyShout] = useState(false);
  const nameContainerRef = useRef(null);
  // 新增：組字狀態
  const [isComposing, setIsComposing] = useState(false);

  // 當 attend 改變，如果不是 'yes'，清空 relationship 與 wantBouquet
  useEffect(() => {
    if (attend !== 'yes') {
      setRelationship('');
      setWantBouquet('');
    }
  }, [attend]);

  // 當 attend 與 relationship 改變時，檢查條件，若不符合則清空 wantBouquet
  useEffect(() => {
    if (!(attend === 'yes' && (relationship === '女方' || relationship === '實驗室'))) {
      setWantBouquet('');
    }
  }, [attend, relationship]);

  // 當 invitationType 改變時，如果是 "電子" 或 "不用"，則清空 invitationDetail 與 address
  useEffect(() => {
    if (invitationType === '電子' || invitationType === '不用') {
      setInvitationDetail('');
      setAddress('');
    }
  }, [invitationType]);

  // 當 invitationDetail 改變時，如果選擇 "婚禮當天拿"，則清空 address
  useEffect(() => {
    if (invitationDetail === '婚禮當天拿') {
      setAddress('');
    }
  }, [invitationDetail]);

  /* ---------------------- Modal 狀態 ---------------------- */
  const [modal, setModal] = useState({
    open: false,
    mode: "general",
    message: "",
    field: "",
    onConfirm: null,
    onCancel: null
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');
  const [electronicModalOpen, setElectronicModalOpen] = useState(false);

  /* ---------------------- 大聲公留言狀態 ---------------------- */
  const [shoutMessages, setShoutMessages] = useState([]);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  useEffect(() => {
    const handleResize = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ---------------------- 讀取資料庫大聲公留言 ---------------------- */
  useEffect(() => {
    fetch('/api/rsvp')
      .then(res => res.json())
      .then(response => {
         const data = response.data || response;
         const messages = data
           .filter(item => item.shout && item.shout.trim() !== "")
           .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
           .map(item => ({
             name: item.name,
             message: item.shout
           }));
         setShoutMessages(messages);
      })
      .catch(error => console.error('Error fetching shout messages:', error));
  }, []);

  /* ---------------------- Modal 處理函式 ---------------------- */
  const handleTextConfirmConfirm = () => {
    if (modalRef.current.field === "privateMessage") {
      setAllowEmptyPrivate(true);
      // 彈出後續提示視窗
      setModal({
      open: true,
      mode: "general",
      message: "已幫您設定成免填悄悄話了，\n真的沒有要填寫嗎？\n現在返回表單頁面，\n後續可以點\"悄悄話:\"位置\n再次開啟填寫模式喔！\n期待妳填寫喔！\n沒錯，你被PUA了！\n啾咪！！",
      field: "privateMessage",
      onConfirm: () =>
        setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
    });
    return; // 結束函式
    } else if (modalRef.current.field === "shout") {
      setAllowEmptyShout(true);
      // 彈出後續提示視窗
      setModal({
      open: true,
      mode: "general",
      message: "已幫您設定成免填大聲公了，\n真的沒有要填寫嗎？\n現在返回表單頁面，\n後續可以點選\"大聲公:\"位置\n再次開啟填寫模式喔！\n期待妳填寫喔！\n沒錯，你被PUA了！\n啾咪！！",
      field: "shout",
      onConfirm: () =>
        setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
    });
    return; // 結束函式
    }
  };
  const handleTextConfirmCancel = () => {
    setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null });
  };
  const handleModalConfirm = () => {
    if (modal.mode === "textConfirm") {
      handleTextConfirmConfirm();
    } else {
      if (modal.onConfirm) modal.onConfirm();
      setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null });
    }
  };
  const handleModalCancel = () => {
    if (modal.mode === "textConfirm") {
      handleTextConfirmCancel();
    } else {
      if (modal.onCancel) modal.onCancel();
    }
  };
  const handleConfirmSubmit = () => {
    setConfirmModalOpen(false);
    finalSubmit();
  };
  const handleCancelSubmit = () => {
    setConfirmModalOpen(false);
  };

const sendModifyWantBouquet = async (newWantBouquet, name) => {
  try {
    const modifyData = {
      action: "modifywantBouquet",
      name, // 已經去除 "改捧花" 字串
      wantBouquet: newWantBouquet // newWantBouquet 為使用者選擇的值 ("yes" 或 "no")
    };
    const res = await fetch('/api/rsvp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(modifyData)
    });
    const data = await res.json();
    if (data.success) {
      setModal({
        open: true,
        mode: "general",
        message: "修改成功",
        field: "attend",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    } else {
      setModal({
        open: true,
        mode: "general",
        message: "修改失敗",
        field: "attend",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
    }
  } catch (error) {
    console.error("修改捧花失敗：", error);
    alert("修改捧花失敗：" + error.message);
  }
};

// 假設在您的元件中新增一個 state
const [newWantBouquet, setNewWantBouquet] = useState(null);
const [showModifyModal, setShowModifyModal] = useState(false);
const [currentWantBouquet, setCurrentWantBouquet] = useState(null);
const [modifiedName, setModifiedName] = useState("");

  // 當使用者在姓名欄位輸入 "thisispassword880522" 時，觸發管理員登入
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (nameField.trim() === "thisispassword880522") {
      setShowAdminLogin(true);
      return;
    }

  // 如果 nameField 包含 "改捧花"，進入修改流程
  if (nameField.includes("改捧花")) {
    try {
      const nameWithoutKeyword = nameField.replace("改捧花", "");
      const searchQuery = new URLSearchParams({
        action: "searchwantBouquet",
        name: nameWithoutKeyword
      });
      const searchRes = await fetch(`/api/rsvp?${searchQuery.toString()}`);
      const searchData = await searchRes.json();
      if (!searchData.success) {
        setModal({
        open: true,
        mode: "general",
        message: `未搜尋到您的名字 "${nameWithoutKeyword}"！\n麻煩您確認一下！`,
        field: "attend",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
      }
      // 從搜尋結果取得現有的捧花狀態
      const fetchedWantBouquet = searchData.data.wantBouquet;
      // 將取得的資料存入 state
      setCurrentWantBouquet(fetchedWantBouquet);
      setModifiedName(nameWithoutKeyword);
      // 顯示專用的修改捧花 modal
      setShowModifyModal(true);
      return; // 結束流程，不走後續送出邏輯
    } catch (error) {
      return;
    }
  }

    if (!nameField.trim()) {
      setModal({
        open: true,
        mode: "general",
        message: "拜託告訴我您的名字！",
        field: "name",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    if (!attend) {
      setModal({
        open: true,
        mode: "general",
        message: "您有要來嗎？",
        field: "attend",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    if (attend === 'yes') {
      if (!relationship) {
        setModal({
          open: true,
          mode: "general",
          message: "請選擇您與新人的關係！",
          field: "relationship",
          onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
        });
        return;
      }
    }
    if (attend === 'yes' && (relationship === '女方' || relationship === '實驗室')) {
      if (!wantBouquet) {
        setModal({
          open: true,
          mode: "general",
          message: "請選擇是否想抽捧花！",
          field: "wantBouquet",
          onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
        });
        return;
      }
    }

    if (attend === 'yes' && (meatCount + vegCount === 0)) {
      setModal({
        open: true,
        mode: "general",
        message: childCount > 0 ? "只有小孩要來嗎？" : "蛤！不是說要來？",
        field: "meals",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    if (!invitationType) {
      setModal({
        open: true,
        mode: "general",
        message: "請選擇喜帖形式！",
        field: "invitation",
        onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    if (invitationType === '實體' || invitationType === '都要') {
      if (!invitationDetail) {
        setModal({
          open: true,
          mode: "general",
          message: "請選擇喜帖獲取方式！",
          field: "invitationDetail",
          onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
        });
        return;
      }
      if ((invitationDetail === '親送' || invitationDetail === '寄送') && !address.trim()) {
        setModal({
          open: true,
          mode: "general",
          message: "請告訴我您要送到哪裡？",
          field: "address",
          onConfirm: () => setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
        });
        return;
      }
    }
    if (!privateMessage.trim() && !allowEmptyPrivate) {
      const newModal = {
      open: true,
      mode: "textConfirm",
      message: "真的不寫悄悄話嗎？",
      field: "privateMessage",
      onConfirm: handleTextConfirmConfirm,
      onCancel: handleTextConfirmCancel
      };
      modalRef.current = newModal; // 暫存到 ref 中
      setModal(newModal);
      return;
    }
    const maxCount = getMaxShoutCount(shout);
    if (shout.length > maxCount) {
      setModal({
        open: true,
        mode: "general",
        message: `大聲公告限${maxCount}個字`,
        field: "shout",
        onConfirm: () =>
          setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    if (!shout.trim() && !allowEmptyShout) {
        const newModal = {
        open: true,
        mode: "textConfirm",
        message: "真的不寫大聲公嗎？",
        field: "shout",
        onConfirm: handleTextConfirmConfirm,
        onCancel: handleTextConfirmCancel
      };
      modalRef.current = newModal; // 暫存到 ref 中
      setModal(newModal);
      return;
    }
    if (!joinLine) {
      setModal({
        open: true,
        mode: "general",
        message: "請選擇加入Line！",
        field: "joinLine",
        onConfirm: () =>
          setModal({ open: false, mode: "general", message: "", field: "", onConfirm: null, onCancel: null })
      });
      return;
    }
    let foodSummary = "\n";
    if (meatCount + vegCount === 1) {
      if (meatCount === 1) {
        foodSummary += "我1人吃肉肉，";
      } else if (vegCount === 1) {
        foodSummary += "我1人菜菜，";
      }
      foodSummary += childCount > 0 
        ? `我要帶 ${childCount} 個小baby，我需要 ${childSeatCount} 個兒童座椅。`
        : `我沒有要帶小baby。`;
    } else {
      foodSummary += meatCount > 0 
        ? `我們有 ${meatCount} 人吃肉肉，` 
        : `我們沒有人吃肉肉，`;
      foodSummary += vegCount > 0 
        ? `我們有 ${vegCount} 人吃菜菜，\n` 
        : `我們沒有人吃菜菜，`;
      foodSummary += childCount > 0 
        ? `我們要帶 ${childCount} 個小baby，我們需要 ${childSeatCount} 個兒童座椅。`
        : `我們沒有要帶小baby。`;
    }
    let invitationSummary = "";
    if (invitationType === "不用") {
      invitationSummary = "我環保愛地球，不拿喜帖。";
    } else if (invitationType === "電子") {
      invitationSummary = "我想要拿電子喜帖。";
    } else if (invitationType === "實體") {
      invitationSummary = `我想要拿實體喜帖，幫我${invitationDetail ? invitationDetail : ""}${address.trim() ? "到" + address : ""}`;
    } else if (invitationType === "都要") {
      invitationSummary = `我想要拿電子喜帖和實體喜帖，幫我${invitationDetail ? invitationDetail : ""}${address.trim() ? "到" + address : ""}`;
    }
    const summary = `
我是${nameField}，${attend === 'yes' 
    ? (relationship === '男方' 
         ? '我哲儀麻吉啦！' 
         : relationship === '女方' 
           ? '我是詩涵一生中很重要的人！' 
           : relationship === '實驗室' 
             ? '我就是實驗室頂梁柱！' 
             : '')
    : ''}
${attend === 'yes' ? '你們的婚禮我一定到，' : '你們的婚禮我很想到，但實在是沒辦法參與，'}${attend === 'yes' && (relationship === '女方' || relationship === '實驗室') ? (wantBouquet === 'yes' ? '\n我超想抽捧花！' : '\n我沒有要抽捧花！') : ''}
${foodSummary}
${invitationSummary}

我想跟新人偷偷說，
${privateMessage}
我想跟新人大聲說：${shout}
我保證我一定會加入Line帳號。
    `;
    setConfirmSummary(summary);
    setConfirmModalOpen(true);
  };

  // 管理員登入處理
  const handleAdminLogin = async () => {
    try {
      // 將 username 與 password 透過 query string 傳送給 /api/rsvp
      const query = new URLSearchParams({
        username: adminUsername,
        password: adminPassword
      });
      const rsvpRes = await fetch(`/api/rsvp?${query.toString()}`);
      const rsvpData = await rsvpRes.json();
      if (!rsvpData.success) {
        throw new Error('取得資料失敗');
      }
      // 如果傳回的資料完整，表示驗證通過
      setIsAdmin(true);
      setShowAdminLogin(false);
      setAllResponses(rsvpData.data || rsvpData);
    } catch (error) {
      console.error('Error during admin login and fetching responses:', error);
      alert('登入失敗：' + error.message);
    }
  };


  const handleAdminCancel = () => {
    setShowAdminLogin(false);
  };

  const handleElectronicSubmit = () => {
    setElectronicModalOpen(false);
    setConfirmModalOpen(true);
  };

  // 送出最終表單，同時若大聲公有填則更新資料庫留言與 state
  const finalSubmit = () => {
    if (shout.trim()) {
      const newMsg = { name: nameField, message: shout };
      setShoutMessages(prev => {
        const insertIndex = currentIndexRef.current + 1; // 取得目前 index 的下一個位置
        return [
          ...prev.slice(0, currentIndexRef.current),
          newMsg,
          ...prev.slice(insertIndex)
        ];
      });
    }
    const formData = {
      name: nameField,
      attendance: attend,
      relationship,
      wantBouquet,
      meatCount,
      vegCount,
      childCount,
      childSeatCount,
      invitationType,
      invitationDetail,
      address,
      privateMessage,
      shout,
      joinLine
    };
    fetch('/api/rsvp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          // 顯示成功提示，點擊確認後依據 invitationType 決定是否開啟電子喜帖預覽
          setModal({
            open: true,
            mode: "general",
            message: "送出成功！",
            onConfirm: () => {
              setModal({
                open: false,
                mode: "general",
                message: "",
                field: "",
                onConfirm: null,
                onCancel: null
              });
              if (invitationType === '電子' || invitationType === '都要') {
                setElectronicModalOpen(true);
              }
            }
          });
        } else {
          setModal({
            open: true,
            mode: "general",
            message: "送出失敗！",
            onConfirm: () =>
              setModal({
                open: false,
                mode: "general",
                message: "",
                field: "",
                onConfirm: null,
                onCancel: null
              })
          });
        }
      })
      .catch(() => {
        setModal({
          open: true,
          mode: "general",
          message: "送出失敗！",
          onConfirm: () =>
            setModal({
              open: false,
              mode: "general",
              message: "",
              field: "",
              onConfirm: null,
              onCancel: null
            })
        });
      });
  };

// 預先載入的第一張圖片
const firstImage = '/images/page0.webp';

// 並行下載的圖片來源，加入 /images/page2.webp 與 albumImages
const parallelImages = [
  '/images/page1.webp',
  '/images/page2.webp',
  '/images/line.svg',
  '/images/Electronic_Wedding_Invitations.jpg',
  ...albumImages
];


  useEffect(() => {
    async function preloadAllImages() {
      // 1. 依序載入第一張圖片
      try {
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.src = firstImage;
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = () => reject(new Error(`載入失敗: ${firstImage}`));
          }
        });
      } catch (err) {
        console.error(err);
        // 若第一張圖片載入失敗，也可以選擇繼續載入其他圖片
      }

      // 2. 並行載入其他圖片（包含 /images/page2.webp 與 albumImages）
      try {
        await Promise.all(
          parallelImages.map(src =>
            new Promise((resolve, reject) => {
              const img = new Image();
              img.src = src;
              if (img.complete) {
                resolve();
              } else {
                img.onload = resolve;
                img.onerror = () => reject(new Error(`載入失敗: ${src}`));
              }
            })
          )
        );
      } catch (err) {
        console.error("並行圖片預載入失敗：", err);
      }

      // 載入完成後，更新狀態
      setIsLoaded(true);
    }

    preloadAllImages();
  }, []);

  // 若尚未載入完成，先顯示 Preloader
  if (!isLoaded) {
    return <Preloader />;
  }

  return (
    <div className="text-white" style={globalFont}>
      {/* 管理員登入 Modal */}
      <AdminLoginModal
        open={showAdminLogin}
        onLogin={handleAdminLogin}
        onCancel={handleAdminCancel}
        adminUsername={adminUsername}
        setAdminUsername={setAdminUsername}
        adminPassword={adminPassword}
        setAdminPassword={setAdminPassword}
      />
      {/* 管理員面板 Modal */}
      <AdminPanelModal
        open={isAdmin}
        responses={allResponses}
        onClose={() => setIsAdmin(false)}
      />

      {/* 第一頁：Landing */}
      <section id="landing" className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            className="relative w-full"
            style={{ aspectRatio: '3/2' }}
            animate={{ opacity: section1Opacity }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          >
            <img src="/images/page1.webp" alt="Landing" className="w-full h-full object-contain" style={{ opacity: section1Opacity }} />
            <div className="absolute bottom-0 left-0 w-full">
              <TabBar />
            </div>
          </motion.div>
          <motion.div
            className="w-full mt-4"
            style={{ whiteSpace: 'pre-wrap', fontFamily: '"Apple LiSu", "Pixel LiSu", "Android LiSu", "LiSu", "隸書", serif' }}
            animate={{ opacity: section1Opacity }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          >
              <div style={{ textAlign: 'center', marginTop: 0, fontSize: '1.5rem' }}>
                <strong>《一生儀詩·三生有幸》</strong>
              </div>
            {storyText.split('\n').map((line, idx) => (
              <p key={idx} className="text-lg my-2">{line}</p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 第二頁：Wedding Info */}
      <section id="info" className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <motion.div className="w-full" animate={{ opacity: section2Opacity }} transition={{ duration: 0.8, ease: 'easeInOut' }}>
            <img src="/images/page2.webp" alt="Wedding Info Banner" className="w-full h-full object-contain" />
            <div className="mt-4 mb-4">
              <TabBar />
            </div>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 flex flex-col items-center justify-center p-4">
                <p className="text-xl leading-relaxed whitespace-pre-line">{weddingInfoText}</p>
                <a
                  href="https://line.me/ti/p/@747bolfu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img
                      src="/images/line.svg"
                      alt="QR Code"
                      style={{ width: '150px', height: 'auto' }}
                      className="mt-2"
                    />
                </a>
              </div>
              <div className="md:w-1/2">
                <div className="relative w-full" style={{ paddingBottom: '75%', height: 0 }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14562.075047487087!2d120.663751!3d24.153533!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x34693d9d07e49ca1%3A0x380e1e9dad8a5b5b!2z5Y-w5Lit5YWo5ZyL5aSn6aOv5bqXIEhvdGVsIE5hdGlvbmFs!5e0!3m2!1szh-TW!2sus!4v1740481229997!5m2!1szh-TW!2sus"
                    className="absolute top-0 left-0 w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 第三頁：Album */}
      <section id="album" className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <motion.div animate={{ opacity: section3Opacity }} transition={{ duration: 0.8, ease: 'easeInOut' }}>
            <div className="mb-2">
              <TabBar />
            </div>
            <div className="w-full">
              <div className="relative mx-auto" style={{ width: '80%', aspectRatio: '3/2', overflow: 'visible', maxWidth: '1024px' }}>
                {renderMainImage()}
                <button onClick={() => setAlbumIndex((prev) => (prev - 1 + albumImages.length) % albumImages.length)}
                  className="absolute top-1/2 left-[-3rem] transform -translate-y-1/2 rounded-full w-10 h-10 flex items-center justify-center bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition text-2xl">
                  &lt;
                </button>
                <button onClick={() => setAlbumIndex((prev) => (prev + 1) % albumImages.length)}
                  className="absolute top-1/2 right-[-3rem] transform -translate-y-1/2 rounded-full w-10 h-10 flex items-center justify-center bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition text-2xl">
                  &gt;
                </button>
              </div>
            </div>
            <div className="mt-4 overflow-x-hidden" style={{ cursor: 'pointer' }}>
              <div className="relative">
                <div className="overflow-x-auto pt-4 pb-4" ref={thumbContainerRef} onMouseEnter={handleThumbMouseEnter} onMouseMove={handleThumbMouseMove} onMouseLeave={handleThumbMouseLeave} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <div className="flex flex-nowrap gap-2">
                    {albumImages.map((img, idx) => (
                      <motion.img
                        key={idx}
                        layout
                        ref={(el) => (thumbRefs.current[idx] = el)}
                        src={img}
                        alt={`縮圖 ${idx + 1}`}
                        className="w-48 h-32 object-cover cursor-pointer transition-all duration-500"
                        onClick={() => {
                          setAlbumIndex(idx);
                          autoStartTimeRef.current = performance.now();
                        }}
                        style={idx === albumIndex ? { outline: '4px solid blue' } : {}}
                      />
                    ))}
                  </div>
                </div>
                <div className="absolute left-0 top-0 h-full w-16 pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(253,87,75,0.8), rgba(253,87,75,0))' }}></div>
                <div className="absolute right-0 top-0 h-full w-16 pointer-events-none" style={{ background: 'linear-gradient(to left, rgba(253,87,75,0.8), rgba(253,87,75,0))' }}></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 第四頁：回填表單 */}
      <section id="form" className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <motion.div animate={{ opacity: section4Opacity }} transition={{ duration: 0.8, ease: 'easeInOut' }}>
            <div className="mb-2">
              <TabBar />
            </div>
            <div className="mb-6 text-center">
              {weddingInfoText.split('\n').map((line, idx) => (
                <p key={idx} className="whitespace-nowrap">{line}</p>
              ))}
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div ref={nameContainerRef} className="flex items-center">
                <label className="w-24 whitespace-nowrap">姓名:</label>
                <input
                  type="text"
                  value={nameField}
                  onChange={(e) => setNameField(e.target.value)}
                  className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900"
                />
              </div>
              <div className="flex items-center">
                <label className="w-24 whitespace-nowrap">是否出席:</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="attendance" value="yes" checked={attend === 'yes'} onChange={() => setAttend('yes')} />
                    <span className="ml-1">是！一定到！</span>
                  </label>
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="attendance" value="no" checked={attend === 'no'} onChange={() => setAttend('no')} />
                    <span className="ml-1">不會！祝幸福</span>
                  </label>
                </div>
              </div>
              {attend === 'yes' && (
                <>
                  <div className="flex items-center">
                    <label className="w-24 whitespace-nowrap">與新人關係:</label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="relationship" value="男方" checked={relationship === '男方'} onChange={() => setRelationship('男方')} />
                        <span className="ml-1">男方</span>
                      </label>
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="relationship" value="女方" checked={relationship === '女方'} onChange={() => setRelationship('女方')} />
                        <span className="ml-1">女方</span>
                      </label>
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="relationship" value="實驗室" checked={relationship === '實驗室'} onChange={() => setRelationship('實驗室')} />
                        <span className="ml-1">實驗室</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {attend === 'yes' && (relationship === '女方' || relationship === '實驗室') && (
                <>
                  <div className="flex items-center">
                    <label className="w-24 whitespace-nowrap">抽捧花:</label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="wantBouquet" value="yes" checked={wantBouquet === 'yes'} onChange={() => setWantBouquet('yes')} />
                        <span className="ml-1">是</span>
                      </label>
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="wantBouquet" value="no" checked={wantBouquet === 'no'} onChange={() => setWantBouquet('no')} />
                        <span className="ml-1">否</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div className={mealGridClass}>
                <div className="flex items-center">
                  <label className="w-16 whitespace-nowrap">葷:</label>
                  <select value={meatCount} onChange={(e) => setMeatCount(parseInt(e.target.value))} className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900">
                    {Array.from({ length: 9 }).map((_, i) => (<option key={i} value={i}>{i}</option>))}
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="w-16 whitespace-nowrap">素:</label>
                  <select value={vegCount} onChange={(e) => setVegCount(parseInt(e.target.value))} className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900">
                    {Array.from({ length: 9 }).map((_, i) => (<option key={i} value={i}>{i}</option>))}
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="w-16 whitespace-nowrap">兒童:</label>
                  <select value={childCount} onChange={(e) => setChildCount(parseInt(e.target.value))} className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900">
                    {Array.from({ length: 6 }).map((_, i) => (<option key={i} value={i}>{i}</option>))}
                  </select>
                </div>
                {childCount > 0 && (
                  <div className="flex items-center">
                    <label className="w-24 whitespace-nowrap">兒童座椅:</label>
                    <select value={childSeatCount} onChange={(e) => setChildSeatCount(parseInt(e.target.value))} className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900">
                      {Array.from({ length: childCount + 1 }).map((_, i) => (<option key={i} value={i}>{i}</option>))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <label className="w-24 whitespace-nowrap">喜帖形式:</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="invitation" value="電子" checked={invitationType === '電子'} onChange={() => setInvitationType('電子')} />
                    <span className="ml-1">電子</span>
                  </label>
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="invitation" value="實體" checked={invitationType === '實體'} onChange={() => setInvitationType('實體')} />
                    <span className="ml-1">實體</span>
                  </label>
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="invitation" value="都要" checked={invitationType === '都要'} onChange={() => setInvitationType('都要')} />
                    <span className="ml-1">都要</span>
                  </label>
                  <label className="flex items-center whitespace-nowrap">
                    <input type="radio" name="invitation" value="不用" checked={invitationType === '不用'} onChange={() => setInvitationType('不用')} />
                    <span className="ml-1">不用</span>
                  </label>
                </div>
              </div>
              {(invitationType === '實體' || invitationType === '都要') && (
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <label className="w-24 whitespace-nowrap">喜帖獲取:</label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="invitationDetail" value="婚禮當天拿" checked={invitationDetail === '婚禮當天拿'} onChange={() => setInvitationDetail('婚禮當天拿')} />
                        <span className="ml-1">婚禮當天拿</span>
                      </label>
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="invitationDetail" value="親送" checked={invitationDetail === '親送'} onChange={() => setInvitationDetail('親送')} />
                        <span className="ml-1">親送</span>
                      </label>
                      <label className="flex items-center whitespace-nowrap">
                        <input type="radio" name="invitationDetail" value="寄送" checked={invitationDetail === '寄送'} onChange={() => setInvitationDetail('寄送')} />
                        <span className="ml-1">寄送</span>
                      </label>
                    </div>
                  </div>
                  {(invitationDetail === '親送' || invitationDetail === '寄送') && (
                    <div className="flex items-center mt-4">
                      <label className="w-24 whitespace-nowrap">地址:</label>
                      <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900" />
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center">
                <label
                  className="w-24 whitespace-nowrap cursor-pointer"
                  onClick={() => {
                    if (allowEmptyPrivate) {
                      setAllowEmptyPrivate(false);
                    }
                  }}
                >
                  悄悄話:
                </label>
                <textarea
                  value={privateMessage}
                  onChange={(e) => setPrivateMessage(e.target.value)}
                  rows={3}
                  style={{ resize: 'none' }}
                  className={`flex-1 p-1 h-20 border rounded bg-gray-100 text-gray-900 ${allowEmptyPrivate ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={allowEmptyPrivate}
                ></textarea>
              </div>
              <div className="flex items-center">
                <label
                  className="w-24 whitespace-nowrap cursor-pointer"
                  onClick={() => {
                    if (allowEmptyShout) {
                      setAllowEmptyShout(false);
                    }
                  }}
                >
                  大聲公:
                </label>
                <input
                  type="text"
                  value={shout}
                  style={{ resize: 'none' }}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={(e) => {
                    setIsComposing(false);
                    const newVal = e.target.value;
                    const maxCount = getMaxShoutCount(newVal);
                    if (newVal.length > maxCount) {
                      setShout(newVal.slice(0, maxCount));
                    } else {
                      setShout(newVal);
                    }
                  }}
                  onChange={(e) => {
                    const newVal = e.target.value;
                    if (!isComposing) {
                      const maxCount = getMaxShoutCount(newVal);
                      if (newVal.length > maxCount) {
                        setShout(newVal.slice(0, maxCount));
                        return;
                      }
                    }
                    setShout(newVal);
                  }}
                  className={`flex-1 p-1 h-8 border rounded bg-gray-100 text-gray-900 ${allowEmptyShout ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder={`限${getMaxShoutCount(shout)}字元`}
                  disabled={allowEmptyShout}
                />
              </div>
              <div className="flex items-center">
                <label className="w-24 whitespace-nowrap">加入Line:</label>
                <label className="flex items-center whitespace-nowrap">
                  <input
                    type="radio"
                    name="joinLine"
                    value="yes"
                    checked={joinLine === true}
                    onChange={() => setJoinLine(true)}
                  />
                  <span className="ml-1">是</span>
                </label>
                <a href="https://line.me/ti/p/@747bolfu" target="_blank" rel="noopener noreferrer" className="ml-4">
                  <img src="/images/line.svg" alt="Line QR Code" className="w-16 h-auto" />
                </a>
              </div>
              <div className="flex justify-center">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">送出</button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
<section id="new-section" className="h-[15vh]">
  {/* 可能是為了iPhone */}
</section>

      {/* 大聲公留言輪播 */}
      <ShoutBox key={isLandscape ? "landscape" : "portrait"} shoutMessages={shoutMessages} isLandscape={isLandscape} currentIndexRef={currentIndexRef} />

      {/* Modal */}
      <CustomModal
        open={modal.open}
        mode={modal.mode}
        message={modal.message}
        onConfirm={modal.onConfirm ? modal.onConfirm : handleModalConfirm}
        onCancel={modal.onCancel ? modal.onCancel : handleModalCancel}
      />

      <ConfirmModal
        open={confirmModalOpen}
        summary={confirmSummary}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
      />

      <ElectronicModal
        open={electronicModalOpen}
        onSubmit={() => setElectronicModalOpen(false)}
        onClose={() => setElectronicModalOpen(false)}
      />

      <ModifyWantBouquetModal
        open={showModifyModal}
        currentValue={currentWantBouquet}
        onConfirm={(selectedValue) => {
          // 使用者在 modal 中確認後，呼叫 API 送出修改請求
          sendModifyWantBouquet(selectedValue, modifiedName);
          setShowModifyModal(false);
        }}
        onCancel={() => setShowModifyModal(false)}
      />
    </div>
  );
}
